import React, { useState } from 'react';
import { companyInfo } from '../data';
import { Star, ArrowRight, ShieldCheck, Send, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Hero() {
  const [status, setStatus] = useState(null); // 'loading', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section className="relative flex items-center pt-32 md:pt-60 lg:pt-48 pb-28 min-h-[700px] overflow-hidden bg-slate-950">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/hero-car.png" 
          alt="Premium Ceramic Coating" 
          className="w-full h-full object-cover opacity-50 scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/60 to-slate-950" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl lg:text-left text-center mx-auto lg:mx-0"
          >
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-white/5 px-3 py-1 rounded-full mb-6 mx-auto lg:mx-0" data-dev-id="src/components/Hero.jsx:50:12"
            >
              <div className="flex text-white">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <span className="text-slate-300 text-sm font-bold tracking-wide uppercase">
                {companyInfo.googleRating} STJERNER PÅ +{companyInfo.reviewCount} ANMELDELSER
              </span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-4xl md:text-6xl font-black text-white leading-[1.1] mb-6 tracking-tight uppercase"
            >
              Giv din bil den ultimative <span className="text-sky-400">beskyttelse</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-xl text-slate-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0"
            >
              Ceramic Coating er ikke bare en behandling – det er en investering i din bils fremtid. 
              Mindre vask, ekstrem glans og beskyttelse mod hverdagens slid.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a 
                href="#kontakt" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group flex items-center justify-center gap-2 bg-sky-400 text-blue-950 hover:bg-sky-500 px-8 py-4 rounded-full font-black text-lg transition-all shadow-xl shadow-sky-400/20 active:scale-95"
              >
                Book tid nu
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a 
                href="#pakker" 
                className="group flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 px-8 py-4 rounded-full font-bold text-lg transition-all active:scale-95"
              >
                Se pakker & priser
              </a>
            </motion.div>
          </motion.div>

          {/* Right Form */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="lg:ml-auto w-full max-w-md mx-auto lg:mx-0"
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 bg-sky-400/10 blur-3xl rounded-full" />
              
              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={48} />
                  </div>
                  <h4 className="text-2xl font-black text-white mb-4 uppercase">Tak for din booking!</h4>
                  <p className="text-slate-300 mb-8">Vi har modtaget din forespørgsel og kontakter dig inden for 24 timer.</p>
                  <button 
                    onClick={() => setStatus(null)}
                    className="bg-white text-slate-950 px-8 py-3 rounded-full font-bold transition-transform active:scale-95"
                  >
                    Lav en ny booking
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h3 className="text-2xl font-black text-white mb-2 uppercase">Book tid eller få tilbud</h3>
                    <p className="text-slate-300 text-sm">Udfyld formularen, så vender vi tilbage med en bekræftelse eller et prisoverslag.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Honeypot */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Website URL</label>
                      <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                    </div>

                    <div>
                      <input 
                        required 
                        name="name"
                        type="text" 
                        placeholder="Dit navn" 
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 transition-all"
                      />
                    </div>

                    <div>
                      <input 
                        required 
                        name="phone"
                        type="tel" 
                        placeholder="Telefonnummer" 
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 transition-all"
                      />
                    </div>

                    <div>
                      <input 
                        required 
                        name="email"
                        type="email" 
                        placeholder="Din e-mail" 
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 transition-all"
                      />
                    </div>

                    <div>
                      <input 
                        name="preferred_date"
                        type="date" 
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/50 transition-all"
                        style={{ colorScheme: 'dark' }}
                      />
                    </div>

                    <div>
                      <select 
                        name="service"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-sky-400/50 transition-all appearance-none cursor-pointer"
                      >
                        <option value="" className="bg-slate-900">Vælg ydelse...</option>
                        <option value="ceramic" className="bg-slate-900">Keramisk Coating</option>
                        <option value="polering" className="bg-slate-900">Polering</option>
                        <option value="vask" className="bg-slate-900">Luksusvask</option>
                      </select>
                    </div>

                    <button 
                      disabled={status === 'loading'}
                      type="submit" 
                      className="w-full bg-sky-400 hover:bg-sky-500 text-blue-950 font-black py-4 rounded-full text-lg shadow-xl shadow-sky-400/20 transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-70 mt-4"
                    >
                      {status === 'loading' ? 'Sender...' : 'Send bookingforespørgsel'}
                      <Send size={18} />
                    </button>
                    
                    {status === 'error' && (
                      <p className="text-red-400 text-center font-bold text-xs">Der skete en fejl. Prøv venligst igen.</p>
                    )}

                    <p className="text-[10px] text-center text-slate-500 mt-4 uppercase tracking-widest font-bold">
                      Svar inden for 24 timer • 100% Uforpligtende
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
